import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useTheme } from '../../styles/ThemeProvider';
import { itemValuationDisplay, type ItemValuationFields } from '../../lib/itemValuation';

/**
 * V3 P6 item valuation (DESIGN_V3 §5.5). Renders ONLY the item's own state via
 * `itemValuationDisplay`: no catalogue headline, no variant history, no network call. The
 * catalogue headline (`MarketValuation`) stays a Buy / catalogue concept.
 */
interface ItemValuationProps {
  item: ItemValuationFields;
  compact?: boolean;
  workPending?: boolean;
}

export function ItemValuation({ item, compact = false, workPending }: ItemValuationProps) {
  const theme = useTheme();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: { marginTop: theme.spacing.xs },
        compactContainer: {
          flexDirection: 'column',
          alignItems: 'flex-end',
          justifyContent: 'center',
        },
        row: { flexDirection: 'row', alignItems: 'baseline', gap: theme.spacing.sm },
        compactPrice: { ...theme.typography.valueSm, color: theme.colors.textPrimary },
        mainPrice: { ...theme.typography.valueSm, color: theme.colors.accent },
        label: {
          ...theme.typography.xs,
          color: theme.colors.textSecondary,
          fontStyle: 'italic',
          letterSpacing: 0,
        },
        unvalued: { ...theme.typography.xs, color: theme.colors.textSecondary, letterSpacing: 0 },
      }),
    [theme]
  );
  const display = itemValuationDisplay(item);
  const analysis = item.image_analysis && typeof item.image_analysis === 'object'
    ? item.image_analysis as Record<string, unknown> : {};
  const low = analysis.valuation_min;
  const high = analysis.valuation_max;

  if (display.value === null) {
    return (
      <View style={compact ? styles.compactContainer : styles.container} testID="item-valuation">
        <Text style={styles.unvalued} testID={`item-valuation-${display.state}`}>
          {item.restoration_valuation && (!item.restoration_pending || workPending === false)
            ? 'Valuation unavailable' : display.label}
        </Text>
      </View>
    );
  }

  const digits = compact && !item.restoration_valuation ? 0 : 2;
  return (
    <View style={compact ? styles.compactContainer : styles.container} testID="item-valuation">
      <View style={styles.row}>
        <Text
          style={compact ? styles.compactPrice : styles.mainPrice}
          testID={`item-valuation-${display.state}`}
        >
          {`$${display.value.toFixed(digits)}`}
        </Text>
        <Text style={styles.label}>{display.label}</Text>
      </View>
      {!compact && item.restoration_valuation && typeof low === 'number' && typeof high === 'number' && (
        <Text style={styles.label}>{`Range: $${low.toFixed(2)} – $${high.toFixed(2)}`}</Text>
      )}
      {!compact && item.restoration_pending && <Text style={styles.label}>Qualified market price unavailable</Text>}
    </View>
  );
}

export default ItemValuation;
